import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Cloud, LogOut, Users, Mail, Eye, BarChart3,
  CheckCircle2, Clock, TrendingUp, Globe
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  company: string | null;
  service: string | null;
  message: string;
  is_read: boolean;
  ip_address: string | null;
  country: string | null;
  city: string | null;
  created_at: string;
}

interface DailyVisits {
  date: string;
  count: number;
}

interface PageStats {
  page_path: string;
  count: number;
}

interface VisitorLocation {
  ip_address: string | null;
  country: string | null;
  city: string | null;
  page_path: string;
  created_at: string;
}

interface LocationStat {
  location: string;
  count: number;
}

const Admin = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [totalVisits, setTotalVisits] = useState(0);
  const [todayVisits, setTodayVisits] = useState(0);
  const [dailyVisits, setDailyVisits] = useState<DailyVisits[]>([]);
  const [pageStats, setPageStats] = useState<PageStats[]>([]);
  const [recentVisitors, setRecentVisitors] = useState<VisitorLocation[]>([]);
  const [topLocations, setTopLocations] = useState<LocationStat[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "submissions" | "analytics">("overview");

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate("/login");
    }
  }, [user, isAdmin, loading, navigate]);

  useEffect(() => {
    if (user && isAdmin) {
      fetchData();
    }
  }, [user, isAdmin]);

  const fetchData = async () => {
    // Fetch contact submissions
    const { data: subs } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (subs) setSubmissions(subs);

    // Fetch total visits
    const { count: total } = await supabase
      .from("page_visits")
      .select("*", { count: "exact", head: true });
    setTotalVisits(total || 0);

    // Fetch today's visits
    const today = new Date().toISOString().split("T")[0];
    const { count: todayCount } = await supabase
      .from("page_visits")
      .select("*", { count: "exact", head: true })
      .gte("created_at", today);
    setTodayVisits(todayCount || 0);

    // Fetch visits for last 7 days for chart
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const { data: recentVisits } = await supabase
      .from("page_visits")
      .select("created_at")
      .gte("created_at", sevenDaysAgo.toISOString());

    if (recentVisits) {
      const grouped: Record<string, number> = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        grouped[d.toISOString().split("T")[0]] = 0;
      }
      recentVisits.forEach((v) => {
        const day = v.created_at.split("T")[0];
        if (grouped[day] !== undefined) grouped[day]++;
      });
      setDailyVisits(Object.entries(grouped).map(([date, count]) => ({
        date: new Date(date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }),
        count,
      })));
    }

    // Fetch page stats
    const { data: pages } = await supabase
      .from("page_visits")
      .select("page_path");
    if (pages) {
      const counts: Record<string, number> = {};
      pages.forEach((p) => {
        counts[p.page_path] = (counts[p.page_path] || 0) + 1;
      });
      setPageStats(
        Object.entries(counts)
          .map(([page_path, count]) => ({ page_path, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10)
      );
    }

    // Recent visitors with location
    const { data: recent } = await supabase
      .from("page_visits")
      .select("ip_address, country, city, page_path, created_at")
      .order("created_at", { ascending: false })
      .limit(25);
    if (recent) setRecentVisitors(recent);

    // Top locations
    const { data: allLoc } = await supabase
      .from("page_visits")
      .select("country, city");
    if (allLoc) {
      const locCounts: Record<string, number> = {};
      allLoc.forEach((v) => {
        if (!v.country && !v.city) return;
        const key = [v.city, v.country].filter(Boolean).join(", ") || "Unknown";
        locCounts[key] = (locCounts[key] || 0) + 1;
      });
      setTopLocations(
        Object.entries(locCounts)
          .map(([location, count]) => ({ location, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10)
      );
    }
  };

  const markAsRead = async (id: string) => {
    await supabase.from("contact_submissions").update({ is_read: true }).eq("id", id);
    setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, is_read: true } : s)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || !isAdmin) return null;

  const unreadCount = submissions.filter((s) => !s.is_read).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
          <div className="flex items-center gap-2 font-heading font-bold text-xl text-foreground">
            <Cloud className="h-7 w-7 text-primary" />
            <span>Cloud<span className="text-primary">ease</span></span>
            <span className="text-xs font-normal text-muted-foreground ml-2 bg-muted px-2 py-0.5 rounded">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{user.email}</span>
            <Button variant="outline" size="sm" onClick={() => { signOut(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-muted p-1 rounded-lg w-fit">
          {[
            { key: "overview" as const, label: "Overview", icon: BarChart3 },
            { key: "submissions" as const, label: `Messages${unreadCount > 0 ? ` (${unreadCount})` : ""}`, icon: Mail },
            { key: "analytics" as const, label: "Analytics", icon: TrendingUp },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Visits", value: totalVisits, icon: Eye, color: "text-primary" },
                { label: "Today's Visits", value: todayVisits, icon: Users, color: "text-accent" },
                { label: "Total Messages", value: submissions.length, icon: Mail, color: "text-primary" },
                { label: "Unread Messages", value: unreadCount, icon: Clock, color: "text-destructive" },
              ].map((stat) => (
                <div key={stat.label} className="bg-card border border-border rounded-xl p-6 shadow-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <p className="text-3xl font-bold font-heading text-foreground">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">Visits (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyVisits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent messages */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">Recent Messages</h3>
              {submissions.length === 0 ? (
                <p className="text-muted-foreground text-sm">No messages yet</p>
              ) : (
                <div className="space-y-3">
                  {submissions.slice(0, 5).map((s) => (
                    <div key={s.id} className={`flex items-start gap-3 p-3 rounded-lg ${!s.is_read ? "bg-primary/5" : "bg-muted/50"}`}>
                      <div className={`h-2 w-2 rounded-full mt-2 ${!s.is_read ? "bg-primary" : "bg-muted-foreground/30"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{s.name}</span>
                          <span className="text-xs text-muted-foreground">{s.email}</span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{s.message}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(s.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Submissions Tab */}
        {activeTab === "submissions" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {submissions.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center shadow-card">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No contact form submissions yet</p>
              </div>
            ) : (
              submissions.map((s) => (
                <div key={s.id} className={`bg-card border rounded-xl p-6 shadow-card ${!s.is_read ? "border-primary/30" : "border-border"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{s.name}</h3>
                        {!s.is_read && <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">New</span>}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {s.email} {s.company && `• ${s.company}`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[s.city, s.country].filter(Boolean).join(", ")}
                        {s.ip_address && (
                          <span className="ml-1">
                            {(s.city || s.country) ? "• " : ""}IP {s.ip_address}
                          </span>
                        )}
                      </p>
                      {s.service && <p className="text-xs text-primary mt-1">{s.service}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(s.created_at).toLocaleString()}
                      </span>
                      {!s.is_read && (
                        <Button size="sm" variant="outline" onClick={() => markAsRead(s.id)}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Read
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{s.message}</p>
                </div>
              ))
            )}
          </motion.div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">Daily Visitors (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyVisits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">
                <Globe className="h-5 w-5 inline-block mr-2" />
                Top Pages
              </h3>
              {pageStats.length === 0 ? (
                <p className="text-sm text-muted-foreground">No page visit data yet</p>
              ) : (
                <div className="space-y-3">
                  {pageStats.map((p) => (
                    <div key={p.page_path} className="flex items-center justify-between">
                      <span className="text-sm text-foreground font-mono">{p.page_path}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(p.count / (pageStats[0]?.count || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground w-12 text-right">{p.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">
                <Globe className="h-5 w-5 inline-block mr-2" />
                Top Visitor Locations
              </h3>
              {topLocations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No location data yet</p>
              ) : (
                <div className="space-y-3">
                  {topLocations.map((l) => (
                    <div key={l.location} className="flex items-center justify-between">
                      <span className="text-sm text-foreground">{l.location}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(l.count / (topLocations[0]?.count || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-foreground w-12 text-right">{l.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <h3 className="text-lg font-semibold font-heading text-foreground mb-4">Recent Visitors</h3>
              {recentVisitors.length === 0 ? (
                <p className="text-sm text-muted-foreground">No visitors yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b border-border">
                        <th className="py-2 pr-4 font-medium">When</th>
                        <th className="py-2 pr-4 font-medium">Page</th>
                        <th className="py-2 pr-4 font-medium">Location</th>
                        <th className="py-2 font-medium">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentVisitors.map((v, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0">
                          <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                            {new Date(v.created_at).toLocaleString()}
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs">{v.page_path}</td>
                          <td className="py-2 pr-4">
                            {[v.city, v.country].filter(Boolean).join(", ") || "—"}
                          </td>
                          <td className="py-2 font-mono text-xs text-muted-foreground">{v.ip_address || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Admin;
